"use client";

import { usePathname } from "next/navigation";
import useSWR from "swr";
import { QueryKeys } from "@/constants";
import { useUserStore } from "@/providers/user-store-provider";
import type { ResponseType } from "@/types/general";
import type { PartnerInfoType } from "@/types/partner-info";

/**
 * The partner object every screen needs: identity, wallet, entitlements,
 * settings (v1: CASApi.info() → stores/Partner.js).
 *
 * Pages used to fetch this under three different keys — "info",
 * "partner_user/info" and "partner_user" — which meant three cache entries and
 * two URLs the gateway does not serve, so `partner.id` silently came back
 * undefined. One hook, one key.
 */
export function usePartnerInfo() {
	const pathname = usePathname();
	const user = useUserStore((state) => state.user);

	// Never ask for partner data before there is a session. This hook is pulled
	// in by the sidebar config and the topbar, which also render on /auth, so an
	// unguarded fetch there 401s -> refreshes -> fails -> logs out -> lands back
	// on /auth and starts over.
	const isAuthenticated = !!user && !pathname?.startsWith("/auth");

	const { data, error, isLoading, mutate } = useSWR<
		ResponseType<PartnerInfoType>
	>(isAuthenticated ? QueryKeys.INFO : null);

	const info = data?.data;

	return {
		info,
		partner: info?.partner,
		partnerId: info?.partner?.id,
		cazhbox: info?.cazhbox,
		currency: info?.country?.currency,
		/** Entitlements — v1 gated menu items and routes on these. */
		hasCardFeature: !!info?.partnerSetting?.featureCard,
		hasRewardFeature: !!info?.partnerSetting?.featureReward,
		isDefaultPackage: !!info?.package?.isDefault,
		isExpiredPackage: !!info?.package?.isExpired,
		isLoading,
		error,
		mutate,
	};
}

export default usePartnerInfo;
